import { Reveal, Overline } from "./Reveal";
import { Check } from "lucide-react";

const scrollToApply = (interest) => {
  const el = document.getElementById("apply");
  if (el) {
    el.scrollIntoView({ behavior: "smooth" });
    window.dispatchEvent(new CustomEvent("prefill-program", { detail: interest }));
  }
};

const oneTime = [
  {
    name: "Custom Workout Program",
    price: "$149",
    tag: "One-Time Purchase",
    desc: "For someone who wants a professional workout program but doesn't need ongoing coaching.",
    features: [
      "One customized 8-week workout program",
      "Gym OR Home version",
      "Beginner, Intermediate or Advanced",
      "Fat Loss OR Muscle Building focus",
      "Exercise substitutions",
      "Cardio recommendations",
      "Delivered as a professional PDF",
      "One revision within 14 days",
    ],
    note: "Not ongoing coaching. No weekly check-ins, messaging, accountability, or nutrition coaching.",
  },
  {
    name: "Nutrition Blueprint",
    price: "$99",
    tag: "One-Time Purchase",
    desc: "For someone who wants nutrition guidance without ongoing coaching.",
    features: [
      "Personalized calorie goal",
      "Protein target",
      "Carb and fat recommendations",
      "Meal templates",
      "Grocery guide",
      "Healthy snack recommendations",
      "Delivered as a professional PDF",
      "One revision within 14 days",
    ],
    note: "Not ongoing coaching. No weekly support or nutrition adjustments after delivery.",
  },
];

const coaching = [
  {
    name: "8-Week Transformation Program",
    price: "$399",
    tag: "One-Time Program · 8 Weeks",
    popular: false,
    desc: "Best for someone who wants guidance, accountability, and support throughout their transformation.",
    features: [
      "Custom 8-week workout program",
      "Nutrition Blueprint",
      "Cardio plan",
      "Weekly progress check-ins",
      "Workout adjustments",
      "Nutrition adjustments during the program",
      "Habit coaching",
      "Accountability",
      "Email/Text support for the full 8 weeks",
    ],
  },
  {
    name: "Elite Online Transformation",
    price: "$299",
    per: "/mo",
    tag: "3 Month Minimum",
    popular: true,
    desc: "Highest level of coaching for clients who want long-term support and accountability.",
    features: [
      "Fully customized workout program",
      "Nutrition guidance",
      "Weekly check-ins",
      "Unlimited workout modifications",
      "Nutrition adjustments",
      "Progress tracking",
      "Video form reviews",
      "Priority messaging",
      "Long-term accountability",
      "Ongoing coaching while enrolled",
    ],
  },
];

const services = [
  {
    name: "Live Virtual Training",
    subtitle: "One-on-One Zoom · 60-Minute Session",
    prices: [
      { label: "1 Session", value: "$75" },
      { label: "4 Session Package", value: "$280" },
      { label: "8 Session Package", value: "$520" },
    ],
    features: [
      "Live 60-minute coaching session",
      "Personalized workout",
      "Form correction",
      "Exercise instruction",
      "Accountability",
      "Time for questions",
    ],
  },
  {
    name: "In-Home Personal Training",
    subtitle: "Savannah, GA Area · 60-Minute Session",
    prices: [
      { label: "1 Session", value: "From $100" },
      { label: "4 Session Package", value: "$380" },
      { label: "8 Session Package", value: "$720" },
    ],
    features: [
      "One-on-one 60-minute workout",
      "Adjustable dumbbells provided",
      "Resistance bands & push-up board",
      "Exercise mat provided",
      "Equipment brought to your home",
      "Form correction & accountability",
    ],
    footer: "No gym membership required. I bring the equipment to you.",
  },
];

const PriceCard = ({ p }) => (
  <div
    data-testid={`pricing-card-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    className={`relative h-full flex flex-col p-8 ${
      p.popular
        ? "bg-[#141414] border-2 border-[#0055FF] shadow-[0_0_40px_rgba(0,85,255,0.18)]"
        : "bg-[#141414] border border-[#262626]"
    }`}
  >
    {p.popular && (
      <span className="absolute -top-3 left-8 bg-[#0055FF] text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5">
        Most Popular
      </span>
    )}
    {p.tag && <span className="text-xs uppercase tracking-widest text-[#0055FF] font-bold">{p.tag}</span>}
    <h3 className="font-display font-semibold text-xl text-white mt-3">{p.name}</h3>
    <div className="mt-4 flex items-end gap-1">
      <span className="font-display font-bold text-4xl text-white">{p.price}</span>
      {p.per && <span className="text-[#A3A3A3] mb-1">{p.per}</span>}
    </div>
    {p.desc && <p className="mt-4 text-sm text-[#A3A3A3] leading-relaxed">{p.desc}</p>}
    <ul className="mt-6 space-y-3 flex-1">
      {p.features.map((f) => (
        <li key={f} className="flex items-start gap-3 text-sm text-white/90">
          <Check size={16} className="text-[#0055FF] mt-0.5 shrink-0" />
          {f}
        </li>
      ))}
    </ul>
    {p.note && (
      <p className="mt-6 text-xs text-[#A3A3A3] border-t border-[#262626] pt-4 leading-relaxed">{p.note}</p>
    )}
    <button
      data-testid={`pricing-apply-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      onClick={() => scrollToApply(p.name)}
      className={`mt-6 w-full font-bold px-6 py-4 uppercase tracking-wide text-sm transition-colors ${
        p.popular
          ? "bg-[#0055FF] text-white hover:bg-[#3377FF]"
          : "bg-transparent border border-[#262626] text-white hover:border-[#0055FF]"
      }`}
    >
      Apply For This
    </button>
  </div>
);

export const Programs = () => {
  return (
    <section id="programs" className="relative py-24 md:py-32 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="max-w-2xl">
          <Overline>Programs & Coaching</Overline>
          <h2 className="font-display font-semibold uppercase tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">
            Choose Your Path To Transformation
          </h2>
          <p className="mt-5 text-[#A3A3A3]">
            Every journey starts with an application — I’ll review it and recommend the
            right program for you.
          </p>
        </Reveal>

        {/* One-time products */}
        <div className="mt-14">
          <h3 className="text-sm uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mb-6">One-Time Products</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {oneTime.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.08}>
                <PriceCard p={p} />
              </Reveal>
            ))}
          </div>
        </div>

        {/* Coaching programs */}
        <div className="mt-16">
          <h3 className="text-sm uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mb-6">Coaching Programs</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            {coaching.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.08}>
                <PriceCard p={p} />
              </Reveal>
            ))}
          </div>
        </div>

        {/* Services */}
        <div className="mt-16">
          <h3 className="text-sm uppercase tracking-[0.2em] text-[#A3A3A3] font-bold mb-6">Live & In-Home Training</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.08}>
                <div
                  data-testid={`service-card-${i}`}
                  className="h-full flex flex-col bg-[#141414] border border-[#262626] p-8"
                >
                  <h3 className="font-display font-semibold text-xl text-white">{s.name}</h3>
                  <p className="text-sm text-[#0055FF] font-semibold mt-1">{s.subtitle}</p>
                  <div className="mt-6 grid grid-cols-3 gap-3 border-y border-[#262626] py-5">
                    {s.prices.map((pr) => (
                      <div key={pr.label}>
                        <div className="font-display font-bold text-lg text-white">{pr.value}</div>
                        <div className="text-[11px] uppercase tracking-wide text-[#A3A3A3] mt-1">{pr.label}</div>
                      </div>
                    ))}
                  </div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-white/90">
                        <Check size={16} className="text-[#0055FF] mt-0.5 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {s.footer && <p className="mt-6 text-xs text-[#A3A3A3] leading-relaxed">{s.footer}</p>}
                  <button
                    data-testid={`service-apply-${i}`}
                    onClick={() => scrollToApply(s.name)}
                    className="mt-6 w-full bg-transparent border border-[#262626] text-white hover:border-[#0055FF] font-bold px-6 py-4 uppercase tracking-wide text-sm transition-colors"
                  >
                    Apply For This
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
